import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from './client';

const ROOT = '/api/v1/oauth-server';
const LOOPBACK_HOSTS = ['localhost', '127.0.0.1', '[::1]'];
// Private-use app scheme in reverse domain-name form (RFC 8252 7.1).
const APP_SCHEME = /^[a-z][a-z0-9+-]*(\.[a-z0-9+-]+)+:$/;

export function useOAuthConsent(requestId) {
  return useQuery({
    queryKey: ['oauth-consent', requestId],
    queryFn: () => apiClient.get(`${ROOT}/requests/${requestId}/`).then(({ data }) => data),
    enabled: Boolean(requestId), retry: false, staleTime: 0,
  });
}

export function useDecideOAuthConsent(requestId) {
  return useMutation({
    mutationFn: ({ consentToken, approve, proof = {} }) => apiClient.post(`${ROOT}/requests/${requestId}/`, {
      consent_token: consentToken, approve, ...(approve ? proof : {}),
    }).then(({ data }) => data),
  });
}

export function useOAuthAccess({ enabled = true } = {}) {
  return useQuery({
    queryKey: ['oauth-access'],
    queryFn: () => apiClient.get(`${ROOT}/access/`).then(({ data }) => data),
    enabled,
  });
}

export function useDelegatedGrants({ enabled = true } = {}) {
  return useQuery({
    queryKey: ['oauth-delegated-grants'],
    queryFn: () => apiClient.get(`${ROOT}/grants/`).then(({ data }) => data),
    enabled,
  });
}

export function useOAuthSettings({ enabled = true } = {}) {
  return useQuery({
    queryKey: ['oauth-settings'],
    queryFn: () => apiClient.get(`${ROOT}/settings/`).then(({ data }) => data),
    enabled,
  });
}

export function useUpdateOAuthSettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (values) => apiClient.patch(`${ROOT}/settings/`, values).then(({ data }) => data),
    onSuccess: (data) => {
      client.setQueryData(['oauth-settings'], data);
      client.invalidateQueries({ queryKey: ['oauth-access'] });
      client.invalidateQueries({ queryKey: ['oauth-delegated-grants'] });
    },
  });
}

export function useRevokeDelegatedGrant() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.post(`${ROOT}/grants/${id}/revoke/`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['oauth-delegated-grants'] }),
  });
}

export function isAllowedCallback(uri) {
  const url = new URL(uri);
  return url.protocol === 'https:'
    || (url.protocol === 'http:' && LOOPBACK_HOSTS.includes(url.hostname))
    || APP_SCHEME.test(url.protocol);
}

export function followOAuthRedirect(uri) {
  if (!isAllowedCallback(uri)) throw new Error('Invalid OAuth callback');
  window.location.assign(new URL(uri).href);
}
